import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-4 md:px-6" aria-label="主导航">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <div className="w-9 h-9 flex items-center justify-center">
              <i className={`ri-ghost-line text-2xl ${isScrolled || !isHome ? 'text-primary-500' : 'text-white'}`}></i>
            </div>
            <span
              className={`font-heading text-xl font-bold transition-colors duration-300 ${
                isScrolled || !isHome ? 'text-foreground-900' : 'text-white'
              }`}
            >
              九妖漫画
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-200 hover:text-primary-500 whitespace-nowrap ${
                isScrolled || !isHome ? 'text-foreground-700' : 'text-white/90'
              } ${location.pathname === '/' ? 'text-primary-500' : ''}`}
            >
              首页
            </Link>

            <div className="relative" onMouseEnter={() => setIsCategoryOpen(true)} onMouseLeave={() => setIsCategoryOpen(false)}>
              <button
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 hover:text-primary-500 whitespace-nowrap ${
                  isScrolled || !isHome ? 'text-foreground-700' : 'text-white/90'
                } ${location.pathname.startsWith('/category') ? 'text-primary-500' : ''}`}
              >
                分类
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </div>
              </button>
              {isCategoryOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                  <div className="bg-background-50 rounded-lg border border-background-200/70 py-2 min-w-[180px] grid grid-cols-2 gap-1 px-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${cat.icon} text-sm`}></i>
                        </div>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors duration-200 hover:text-primary-500 whitespace-nowrap ${
                isScrolled || !isHome ? 'text-foreground-700' : 'text-white/90'
              } ${location.pathname === '/about' ? 'text-primary-500' : ''}`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
                <i className="ri-search-line text-sm text-foreground-400"></i>
              </div>
              <input
                type="text"
                placeholder="搜索漫画..."
                className="pl-9 pr-4 py-2 text-sm bg-background-100 border border-background-200 rounded-full text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 w-[200px] transition-all"
              />
            </div>
          </div>

          <button
            className="md:hidden flex items-center justify-center w-10 h-10"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="菜单"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`text-xl ${isScrolled || !isHome ? 'text-foreground-700' : 'text-white'} ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </div>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 py-4 px-4">
            <div className="flex flex-col gap-3">
              <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 py-2 whitespace-nowrap">首页</Link>
              <div className="text-sm font-medium text-foreground-700 py-2 whitespace-nowrap">分类</div>
              <div className="grid grid-cols-3 gap-2 pl-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1 text-xs text-foreground-600 hover:text-primary-500 py-1 whitespace-nowrap"
                  >
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
              <Link to="/about" className="text-sm font-medium text-foreground-700 hover:text-primary-500 py-2 whitespace-nowrap">关于我们</Link>
              <div className="relative mt-2">
                <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
                  <i className="ri-search-line text-sm text-foreground-400"></i>
                </div>
                <input
                  type="text"
                  placeholder="搜索漫画..."
                  className="pl-9 pr-4 py-2 text-sm w-full bg-background-100 border border-background-200 rounded-full text-foreground-700 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}